import React, { useState, useEffect } from 'react';
import StatCard from './StatCard';
import {
  TrendingUp,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Download,
  Filter,
  RefreshCw,
  Bell,
  Target,
  Zap,
  Award,
  DollarSign,
  ArrowUp,
  ArrowDown,
  Minus,
  MoreVertical,
  ChevronRight,
  Eye,
  Settings,
  HelpCircle
} from 'lucide-react';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ExecutiveDashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');
  // const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [notifications] = useState(3);

  // Simular atualização em tempo real
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        // Atualizar dados
        console.log('Atualizando dashboard...');
      }, 30000); // Atualiza a cada 30 segundos
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // KPIs principais
  const kpis = [
    {
      title: 'Total de Denúncias',
      value: '1,247',
      change: '+12.5%',
      trend: 'up',
      icon: <FileText className="w-6 h-6" />,
      color: 'blue',
      sparkline: [65, 70, 68, 72, 75, 80, 85, 88],
      target: '1,500',
      targetProgress: 83
    },
    {
      title: 'Taxa de Resolução',
      value: '87.3%',
      change: '+5.2%',
      trend: 'up',
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'green',
      sparkline: [75, 78, 80, 82, 85, 86, 87, 87.3],
      target: '90%',
      targetProgress: 97
    },
    {
      title: 'Tempo Médio de Resposta',
      value: '2.4h',
      change: '-18%',
      trend: 'down',
      icon: <Clock className="w-6 h-6" />,
      color: 'purple',
      sparkline: [3.2, 3.0, 2.8, 2.6, 2.5, 2.4, 2.4, 2.4],
      target: '2h',
      targetProgress: 80,
      inverse: true
    },
    {
      title: 'Satisfação do Cliente',
      value: '4.8/5',
      change: '+0.3',
      trend: 'up',
      icon: <Award className="w-6 h-6" />,
      color: 'amber',
      sparkline: [4.2, 4.3, 4.5, 4.6, 4.7, 4.7, 4.8, 4.8],
      target: '5.0',
      targetProgress: 96
    },
    {
      title: 'Casos Críticos',
      value: '23',
      change: '-8',
      trend: 'down',
      icon: <AlertTriangle className="w-6 h-6" />,
      color: 'red',
      sparkline: [35, 32, 30, 28, 25, 24, 23, 23],
      target: '< 20',
      targetProgress: 85,
      inverse: true
    },
    {
      title: 'ROI Estimado',
      value: '$247K',
      change: '+22%',
      trend: 'up',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'emerald',
      sparkline: [180, 190, 200, 210, 220, 235, 240, 247],
      target: '$300K',
      targetProgress: 82
    }
  ];

  // Dados para gráfico de linha (tendência temporal)
  const lineChartData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Denúncias Recebidas',
        data: [165, 180, 175, 190, 210, 225, 240],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Denúncias Resolvidas',
        data: [145, 165, 160, 175, 195, 210, 220],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Dados para gráfico de barras (por categoria)
  const barChartData = {
    labels: ['Assédio', 'Fraude', 'Discriminação', 'Corrupção', 'Segurança', 'Outros'],
    datasets: [
      {
        label: 'Este Mês',
        data: [45, 38, 28, 22, 18, 15],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderRadius: 8
      },
      {
        label: 'Mês Anterior',
        data: [40, 35, 30, 25, 20, 12],
        backgroundColor: 'rgba(156, 163, 175, 0.5)',
        borderRadius: 8
      }
    ]
  };

  // Dados para gráfico de rosca (status)
  const doughnutChartData = {
    labels: ['Resolvidas', 'Em Análise', 'Pendentes', 'Arquivadas'],
    datasets: [{
      data: [65, 20, 10, 5],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(156, 163, 175, 0.8)'
      ],
      borderWidth: 0
    }]
  };

  // Dados para gráfico radar (performance por departamento)
  const radarChartData = {
    labels: ['Velocidade', 'Qualidade', 'Comunicação', 'Resolução', 'Satisfação', 'Compliance'],
    datasets: [{
      label: 'Atual',
      data: [85, 90, 75, 88, 92, 95],
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      pointBackgroundColor: 'rgba(59, 130, 246, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
    }, {
      label: 'Meta',
      data: [90, 85, 85, 90, 90, 100],
      backgroundColor: 'rgba(156, 163, 175, 0.1)',
      borderColor: 'rgba(156, 163, 175, 0.5)',
      pointBackgroundColor: 'rgba(156, 163, 175, 0.5)',
      pointBorderColor: '#fff',
      borderDash: [5, 5]
    }]
  };

  // Atividades recentes
  const recentActivities = [
    {
      id: 1,
      type: 'critical',
      title: 'Denúncia Crítica Recebida',
      description: 'Caso #1247 - Assédio moral reportado no departamento de vendas',
      time: '5 min atrás',
      icon: <AlertTriangle className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 2,
      type: 'resolved',
      title: 'Caso Resolvido',
      description: 'Caso #1243 - Investigação concluída com sucesso',
      time: '15 min atrás',
      icon: <CheckCircle className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 3,
      type: 'update',
      title: 'Atualização de Status',
      description: 'Caso #1245 - Movido para análise jurídica',
      time: '1 hora atrás',
      icon: <Activity className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 4,
      type: 'new',
      title: 'Nova Denúncia',
      description: 'Caso #1246 - Irregularidade financeira detectada',
      time: '2 horas atrás',
      icon: <FileText className="w-4 h-4" />,
      color: 'purple'
    }
  ];

  // Top performers (equipes/departamentos)
  const topPerformers = [
    { name: 'Equipe Compliance', score: 98, cases: 234, trend: 'up' },
    { name: 'RH Investigação', score: 95, cases: 189, trend: 'up' },
    { name: 'Jurídico', score: 92, cases: 156, trend: 'stable' },
    { name: 'Auditoria Interna', score: 88, cases: 142, trend: 'down' },
    { name: 'Segurança', score: 85, cases: 98, trend: 'up' }
  ];

  // Função para renderizar mini sparkline
  const renderSparkline = (data, color) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    const width = 80;
    const height = 30;
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <svg width={width} height={height} className="inline-block">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Profissional */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Executivo</h1>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                Ao Vivo
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Filtro de período */}
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="24h">Últimas 24 horas</option>
                <option value="7d">Últimos 7 dias</option>
                <option value="30d">Últimos 30 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="1y">Último ano</option>
                <option value="custom">Personalizado</option>
              </select>
              
              {/* Auto refresh */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`p-2 rounded-lg transition ${
                  autoRefresh ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}
                title="Auto-atualização"
              >
                <RefreshCw className={`w-5 h-5 ${autoRefresh ? 'animate-spin' : ''}`} />
              </button>
              
              {/* Notificações */}
              <button className="relative p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                <Bell className="w-5 h-5 text-gray-600" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>
              
              {/* Exportar */}
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Exportar Relatório</span>
              </button>
              
              {/* Settings */}
              <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                <Settings className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <div className="p-6">
        {/* KPIs Grid */}
        <div 
          className="gap-4 mb-6" 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem'
          }}
        >
          {kpis.map((kpi, index) => (
            <StatCard
              key={index}
              title={kpi.title}
              value={kpi.value}
              icon={kpi.icon}
              color={kpi.color}
              trend={kpi.trend}
              trendValue={kpi.change?.replace('%', '')}
              subtitle={`Meta: ${kpi.target} (${kpi.targetProgress}%)`}
              onClick={() => setSelectedMetric(kpi)}
            />
          ))}
        </div>

        {/* Gráficos Principais */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Tendência Temporal */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Tendência de Denúncias</h2>
              <div className="flex items-center space-x-2">
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Filter className="w-4 h-4 text-gray-500" />
                </button>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Eye className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            <Line
              data={lineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 15
                    }
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: 12,
                    cornerRadius: 8,
                    titleFont: { size: 14, weight: 'bold' },
                    bodyFont: { size: 13 }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      drawBorder: false,
                      color: 'rgba(0, 0, 0, 0.05)'
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    }
                  }
                }
              }}
              height={300}
            />
          </div>

          {/* Distribuição por Categoria */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Por Categoria</h2>
              <div className="flex items-center space-x-2">
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Filter className="w-4 h-4 text-gray-500" />
                </button>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Eye className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            <Bar
              data={barChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 15
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      drawBorder: false,
                      color: 'rgba(0, 0, 0, 0.05)'
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    }
                  }
                }
              }}
              height={300}
            />
          </div>
        </div>

        {/* Segunda linha de gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Status Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status das Denúncias</h2>
            <div className="relative">
              <Doughnut
                data={doughnutChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        usePointStyle: true,
                        padding: 10,
                        font: { size: 11 }
                      }
                    }
                  },
                  cutout: '70%'
                }}
                height={250}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">87%</p>
                  <p className="text-sm text-gray-500">Resolvidas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Radar */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Geral</h2>
            <Radar
              data={radarChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 10,
                      font: { size: 11 }
                    }
                  }
                },
                scales: {
                  r: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                      stepSize: 20
                    }
                  }
                }
              }}
              height={250}
            />
          </div>

          {/* Top Performers */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Top Equipes</h2>
              <Trophy className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="space-y-3">
              {topPerformers.map((performer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-orange-600' : 
                      'bg-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{performer.name}</p>
                      <p className="text-xs text-gray-500">{performer.cases} casos</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-gray-900">{performer.score}</span>
                    {performer.trend === 'up' ? (
                      <ArrowUp className="w-4 h-4 text-green-500" />
                    ) : performer.trend === 'down' ? (
                      <ArrowDown className="w-4 h-4 text-red-500" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Atividades Recentes e Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Atividades Recentes */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Atividade em Tempo Real</h2>
              <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs font-medium">
                Atualizado agora
              </span>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition">
                  <div className={`p-2 rounded-lg bg-${activity.color}-100`}>
                    <div className={`text-${activity.color}-600`}>{activity.icon}</div>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-2">{activity.time}</p>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2 text-center text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm font-medium">
              Ver todas as atividades
            </button>
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <Zap className="w-5 h-5 mr-2" />
                Insights com IA
              </h2>
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">
                Powered by AI
              </span>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/10 backdrop-blur rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="p-1.5 bg-yellow-400 rounded">
                    <AlertTriangle className="w-4 h-4 text-yellow-900" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Padrão Detectado</p>
                    <p className="text-sm text-white/80">
                      Aumento de 23% em denúncias de assédio no departamento de vendas nos últimos 15 dias.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-white/10 backdrop-blur rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="p-1.5 bg-green-400 rounded">
                    <TrendingUp className="w-4 h-4 text-green-900" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Melhoria Identificada</p>
                    <p className="text-sm text-white/80">
                      Tempo de resolução melhorou 18% após implementação do novo protocolo.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-white/10 backdrop-blur rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="p-1.5 bg-blue-400 rounded">
                    <Target className="w-4 h-4 text-blue-900" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Recomendação</p>
                    <p className="text-sm text-white/80">
                      Considere treino adicional para equipe de RH baseado nos tipos de casos recentes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <button className="w-full mt-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur rounded-lg transition text-sm font-medium">
              Ver análise completa com IA
            </button>
          </div>
        </div>
      </div>

      {/* Footer com informações adicionais */}
      <footer className="bg-white border-t border-gray-200 mt-8 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm text-gray-500">
            <span>Última atualização: {new Date().toLocaleString('pt-BR')}</span>
            <span>•</span>
            <span>Próxima sincronização em: 29s</span>
            <span>•</span>
            <span>Versão: 2.1.0</span>
          </div>
          <div className="flex items-center space-x-4">
            <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center">
              <HelpCircle className="w-4 h-4 mr-1" />
              Ajuda
            </button>
            <button className="text-sm text-gray-600 hover:text-gray-900">
              Documentação API
            </button>
            <button className="text-sm text-gray-600 hover:text-gray-900">
              Suporte
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Componente auxiliar para Trophy icon (não existe no lucide-react)
const Trophy = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v1a1 1 0 001 1h1.5a1.5 1.5 0 110 3H4a1 1 0 00-1 1v1a2 2 0 002 2h1.227A4.002 4.002 0 0010 17a4.002 4.002 0 003.773-3H15a2 2 0 002-2v-1a1 1 0 00-1-1h-1.5a1.5 1.5 0 110-3H16a1 1 0 001-1V5a2 2 0 00-2-2H5zm5 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);

export default ExecutiveDashboard;